import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  onGameInvitation, 
  onInvitationResponse,
  respondToInvitation
} from '../utils/socketUtils';
import '../styles/GameInvitations.css';

function GameInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for incoming game invitations
    const removeInvitationListener = onGameInvitation((invitation) => {
      console.log('Received game invitation:', invitation);
      
      // Add the invitation to our state
      setInvitations(prev => {
        // Check if we already have this invitation
        if (prev.some(inv => inv.invitationId === invitation.invitationId)) {
          return prev;
        }
        return [...prev, invitation];
      });
      
      // Play notification sound
      try {
        const audio = new Audio('/sounds/notification.mp3');
        audio.play().catch(err => console.error('Error playing sound:', err));
      } catch (error) {
        console.error('Error with notification sound:', error);
      }
    });

    // Listen for responses to our invitations
    const removeResponseListener = onInvitationResponse((response) => {
      console.log('Received invitation response:', response);
      
      // Remove from sent invitations list
      setSentInvitations(prev => 
        prev.filter(inv => inv.invitationId !== response.invitationId)
      );

      if (response.accepted) {
        // Navigate to the game if accepted
        navigate(`/multiplayer/${response.gameId}`);
      }
    });

    return () => {
      removeInvitationListener();
      removeResponseListener();
    };
  }, [navigate]);

  const handleAcceptInvitation = (invitation) => {
    console.log('Accepting invitation:', invitation);
    
    // Send accept response
    respondToInvitation({
      invitationId: invitation.invitationId,
      fromUserId: invitation.fromUserId,
      accepted: true,
      gameId: invitation.gameId
    });

    // Remove from invitations list
    setInvitations(prev => 
      prev.filter(inv => inv.invitationId !== invitation.invitationId)
    );

    // Navigate to the game
    navigate(`/multiplayer/${invitation.gameId}`);
  };

  const handleDeclineInvitation = (invitation) => {
    console.log('Declining invitation:', invitation);
    
    // Send decline response
    respondToInvitation({
      invitationId: invitation.invitationId,
      fromUserId: invitation.fromUserId,
      accepted: false,
      gameId: invitation.gameId
    });

    // Remove from invitations list
    setInvitations(prev => 
      prev.filter(inv => inv.invitationId !== invitation.invitationId)
    );
  };

  // If no invitations, don't render anything
  if (invitations.length === 0 && sentInvitations.length === 0) {
    return null;
  }

  return (
    <div className="game-invitations-container">
      {invitations.length > 0 && (
        <div className="invitations-section">
          <h3>Game Invitations</h3>
          <ul className="invitations-list">
            {invitations.map((invitation) => (
              <li key={invitation.invitationId} className="invitation-item">
                <div className="invitation-info">
                  <strong>{invitation.fromUsername}</strong> has invited you to play Hangman!
                </div>
                <div className="invitation-actions">
                  <button 
                    className="accept-btn"
                    onClick={() => handleAcceptInvitation(invitation)}
                  >
                    Accept
                  </button>
                  <button 
                    className="decline-btn"
                    onClick={() => handleDeclineInvitation(invitation)}
                  >
                    Decline
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sentInvitations.length > 0 && (
        <div className="invitations-section">
          <h3>Pending Invitations</h3>
          <ul className="invitations-list">
            {sentInvitations.map((invitation) => (
              <li key={invitation.invitationId} className="invitation-item pending">
                <div className="invitation-info">
                  Waiting for <strong>{invitation.toUsername}</strong> to respond...
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Function to add a sent invitation to the component's state
export const addSentInvitation = (invitation) => {
  // This is a workaround since we can't directly access the component's state
  // In a real app, you'd use a state management solution like Redux or Context
  const event = new CustomEvent('sentInvitation', { detail: invitation });
  window.dispatchEvent(event);
};

export default GameInvitations; 